import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useAuth } from "./AuthContext";
import {
  fetchLog2simGeneration,
  type Log2simEventGeneration,
} from "@/services/log2simApi";
import { showToast } from "@/services/toast";

// Global, app-wide tracker for log2sim simulation renders. Mirrors
// DatasetExportJobsContext: a root-mounted provider that polls in-flight jobs
// and fires a deep-linked toast on completion, so the "your simulation is
// ready" notice reaches the user wherever they are in the app (while a tab is
// open — this is client polling, not server push).

const STORAGE_VERSION = "v1";
const MAX_TRACKED_JOBS = 20;
const POLL_INTERVAL_MS = 5_000;
const TERMINAL_STATUSES = new Set<string>(["succeeded", "failed"]);

export interface TrackedLog2simJob extends Log2simEventGeneration {
  /** Human label for the toast, e.g. "Shift left 2 m — actor 7". */
  label: string;
  /** Deep link to the source video's Workbench (the sim shows as a Render). */
  href: string;
  completionNotifiedAt?: string | null;
}

export interface Log2simJobMetadata {
  label: string;
  href: string;
}

interface Log2simJobsContextValue {
  jobs: TrackedLog2simJob[];
  registerJob: (job: Log2simEventGeneration, metadata: Log2simJobMetadata) => void;
  dismissJob: (generationId: string) => void;
}

const Log2simJobsContext = createContext<Log2simJobsContextValue | null>(null);

function storageKey(uid: string): string {
  return `log2sim-jobs:${STORAGE_VERSION}:${uid}`;
}

function isTerminal(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

function loadJobs(uid: string): TrackedLog2simJob[] {
  try {
    const value = window.localStorage.getItem(storageKey(uid));
    if (!value) return [];
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (job): job is TrackedLog2simJob =>
          Boolean(job) &&
          typeof job.generation_id === "string" &&
          typeof job.status === "string" &&
          typeof job.href === "string",
      )
      .slice(0, MAX_TRACKED_JOBS);
  } catch {
    return [];
  }
}

function updatedTimestamp(job: TrackedLog2simJob): number {
  const value = job.completed_at || job.started_at || job.created_at;
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

function sortJobs(jobs: TrackedLog2simJob[]): TrackedLog2simJob[] {
  return [...jobs]
    .sort((left, right) => updatedTimestamp(right) - updatedTimestamp(left))
    .slice(0, MAX_TRACKED_JOBS);
}

export function Log2simJobsProvider({ children }: { children: ReactNode }) {
  const { user, getToken } = useAuth();
  const [jobs, setJobs] = useState<TrackedLog2simJob[]>([]);
  const [hydratedUid, setHydratedUid] = useState<string | null>(null);
  const pollInFlightRef = useRef(false);
  const jobsRef = useRef<TrackedLog2simJob[]>([]);
  const notifiedIdsRef = useRef(new Set<string>());

  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  useEffect(() => {
    setHydratedUid(null);
    if (!user?.uid) {
      setJobs([]);
      return;
    }
    const storedJobs = loadJobs(user.uid);
    storedJobs.forEach((job) => {
      if (job.completionNotifiedAt) notifiedIdsRef.current.add(job.generation_id);
    });
    setJobs(storedJobs);
    setHydratedUid(user.uid);
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || hydratedUid !== user.uid) return;
    try {
      // output_url is a signed (bearer) URL — keep it only in memory.
      const safeJobs = jobs.map((job) => ({ ...job, output_url: null }));
      window.localStorage.setItem(storageKey(user.uid), JSON.stringify(safeJobs));
    } catch {
      // Tracking is a convenience; a storage/privacy failure must not break
      // the simulation itself.
    }
  }, [hydratedUid, jobs, user?.uid]);

  const registerJob = useCallback(
    (job: Log2simEventGeneration, metadata: Log2simJobMetadata) => {
      const tracked: TrackedLog2simJob = {
        ...job,
        ...metadata,
        completionNotifiedAt: null,
      };
      setJobs((current) =>
        sortJobs([
          tracked,
          ...current.filter((existing) => existing.generation_id !== job.generation_id),
        ]),
      );
    },
    [],
  );

  const dismissJob = useCallback((generationId: string) => {
    setJobs((current) => current.filter((job) => job.generation_id !== generationId));
  }, []);

  const activeIds = useMemo(
    () => jobs.filter((job) => !isTerminal(job.status)).map((job) => job.generation_id),
    [jobs],
  );
  const activeKey = activeIds.join("|");

  useEffect(() => {
    if (!user?.uid || hydratedUid !== user.uid || !activeKey) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled || pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const ids = activeKey.split("|").filter(Boolean);
        const results = await Promise.allSettled(
          ids.map((generationId) => fetchLog2simGeneration(generationId, token)),
        );
        if (cancelled) return;

        const responses = new Map<string, Log2simEventGeneration>();
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            responses.set(result.value.generation_id, result.value);
          }
        });
        if (!responses.size) return;

        const currentById = new Map(jobsRef.current.map((job) => [job.generation_id, job]));
        const completed = Array.from(responses.values())
          .filter((response) => isTerminal(response.status))
          .flatMap((response) => {
            const previous = currentById.get(response.generation_id);
            if (
              !previous ||
              isTerminal(previous.status) ||
              previous.completionNotifiedAt ||
              notifiedIdsRef.current.has(response.generation_id)
            ) {
              return [];
            }
            notifiedIdsRef.current.add(response.generation_id);
            return [{ ...previous, ...response }];
          });

        setJobs((current) =>
          sortJobs(
            current.map((job) => {
              const response = responses.get(job.generation_id);
              if (!response) return job;
              const justFinished = !isTerminal(job.status) && isTerminal(response.status);
              const next: TrackedLog2simJob = { ...job, ...response };
              if (justFinished && !job.completionNotifiedAt) {
                next.completionNotifiedAt = new Date().toISOString();
              }
              return next;
            }),
          ),
        );

        completed.forEach((job) => {
          if (job.status === "failed") {
            showToast(
              `${job.label} simulation failed${job.error ? `: ${job.error}` : "."}`,
              "error",
              { toastId: `log2sim-complete-${job.generation_id}` },
            );
            return;
          }
          showToast(`${job.label} simulation is ready.`, "success", {
            toastId: `log2sim-complete-${job.generation_id}`,
            actionLabel: "View in Workbench",
            actionHref: job.href,
            autoClose: false,
          });
        });
      } finally {
        pollInFlightRef.current = false;
      }
    };

    void poll();
    const interval = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeKey, getToken, hydratedUid, user?.uid]);

  const value = useMemo(
    () => ({ jobs, registerJob, dismissJob }),
    [dismissJob, jobs, registerJob],
  );

  return (
    <Log2simJobsContext.Provider value={value}>{children}</Log2simJobsContext.Provider>
  );
}

// Contexts intentionally export their matching hook from the same module.
// eslint-disable-next-line react-refresh/only-export-components
export function useLog2simJobs(): Log2simJobsContextValue {
  const context = useContext(Log2simJobsContext);
  if (!context) {
    throw new Error("useLog2simJobs must be used inside Log2simJobsProvider");
  }
  return context;
}
